import { useEffect, useState } from "react";
import {
    type UscCarDeliveryConfig,
    CarDeliveryDestination,
    CarDeliveryNavStep,
    fetchDeliveryCostPrices,
} from "../utils/carDeliveryUtils";
import {
    setCarDeliveryConfig,
    setCarDeliveryError,
    setCarDeliveryModalNavStep,
    setTempCarDeliveryConfig,
} from "../redux/actions/UscCarDeliveryActions";
import { hideModal } from "../../../common-deprecated/redux/actions/ModalActions";
import { useDebouncedEffect } from "../../../common-deprecated/hooks";
import { toyotaMap } from "../../../common-deprecated/ToyotaMap";
import type { CoordinateType, DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import { getAPI } from "../../../common-deprecated/settings/utils/commonSettingUtils";
import Debug from "../../../common-deprecated/Debug";
import type {
    CarDeliveryDealerSearchRequestBodyType,
    CarDeliveryDealerSearchResultType,
} from "../server/fetchCarDeliveryDealerSearchResult";
import type { MapboxResultType } from "../../../common-deprecated/types/ToyotaMapTypes";
import { type UscDispatchType, useUscSelector } from "../redux/store";
import { useUscLabel } from "../utils/labels";
import { MODAL_USC_CAR_DELIVERY } from "../utils/modalConstants";
import { trackCarDeliveryConfirmDelivery } from "../utils/tracking";
import { useDispatch } from "../../../shared-logic/redux/store";

export type FormattedMapboxResultType = {
    displayAddress: string; // Used to show the address in the search results.
    city: string;
    address: string;
    zip: string;
    country: string;
    geo: CoordinateType;
};

type UseCarDeliveryConfiguratorReturnType = {
    mapboxResults: FormattedMapboxResultType[];
    dealerResults: DealerResultType[];
    currentDealer: DealerResultType | null;
    searchQuery: string;
    loading: boolean;
    errorLabel: string | null;
    onSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchInputClear: () => void;
    dealerLocationClick: (result: DealerResultType) => void;
    mapboxLocationClick: (result: FormattedMapboxResultType) => void;
    removeDeliveryLocation: () => void;
    handleBackButtonClick: () => void;
    confirmDestinationConfiguration: () => Promise<void>;
    confirmTransportationConfiguration: () => Promise<void>;
};

// To store the mapbox results, we need to convert the results to an object containing city, zip, and address.
// We can do this using the context property of the mapbox result. 🤢
const formatMapboxResults = (results: MapboxResultType[]): FormattedMapboxResultType[] | null => {
    // Filter out any results that don't have a context property.
    const filteredResults = results.filter((result) => result.context);

    if (filteredResults.length === 0) return null;

    const formattedResults = filteredResults.map((result) => {
        // We filtered out the results that don't have a context property, so we can safely assume that it exists and use the ! operator.
        const city = result.context!.find((context) => context.id.includes("locality"));
        const zip = result.context!.find((context) => context.id.includes("postcode"));
        const address = result.context!.find((context) => context.id.includes("place"));
        const country = result.context!.find((context) => context.id.includes("country"));

        return {
            displayAddress: result.place_name,
            city: city ? city.text : "",
            zip: zip ? zip.text : "",
            address: address ? address.text : "",
            country: country ? country.text : "",
            geo: {
                lat: result.center[1],
                lon: result.center[0],
            },
        };
    });

    return formattedResults;
};

// Execute a request to the dealer search API.
const fetchDealers = async (
    url: string,
    body: CarDeliveryDealerSearchRequestBodyType,
): Promise<CarDeliveryDealerSearchResultType> => {
    const result = await fetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
    });

    return result.status === 200 ? result.json() : { results: [] };
};

// This hook is similar to the useLocationFilter hook, so most logic is repeated.
// There's still some differences, so it's justifiable to keep them separate.
const useCarDeliveryConfigurator = (): UseCarDeliveryConfiguratorReturnType => {
    const dispatch = useDispatch<UscDispatchType>();

    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [mapboxResults, setMapboxResults] = useState<FormattedMapboxResultType[]>([]);
    const [dealerResults, setDealerResults] = useState<DealerResultType[]>([]);
    const [errorLabel, setErrorLabel] = useState<string | null>(null);

    const deliveryDestination = useUscSelector((state) => state.carDelivery.deliveryDestination);
    const deliveryTransportation = useUscSelector((state) => state.carDelivery.deliveryTransportation);
    const currentDealer = useUscSelector((state) => state.dealer.dealer);
    const tempDeliveryConfig = useUscSelector((state) => state.carDelivery.tempDeliveryConfig);
    const uscEnv = useUscSelector((state) => state.vehicle.type);
    const deliveryConfig = useUscSelector((state) => state.carDelivery.deliveryConfig);
    const navStep = useUscSelector((state) => state.carDelivery.modal.navStep);
    const apiUrl = useUscSelector((state) => `${getAPI(state.commonSettings, "delivery-dealers")}`);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const { country, mapboxToken } = commonSettings;

    const [noResultsLabel, resultsErrorLabel] = useUscLabel([
        "uscCarDeliveryDealerResultsEmpty",
        "uscCarDeliveryDealerResultsError",
    ]);

    const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>): void =>
        setSearchQuery(event.target.value);

    // When switching between delivery destinations, we reset everything.
    // Otherwise, this results in, for example, dealer specific results showing
    // when the user is searching only for a home delivery location.
    useEffect(() => {
        if (tempDeliveryConfig?.destination !== deliveryDestination) {
            setDealerResults([]);
            setMapboxResults([]);
            setSearchQuery("");
            setErrorLabel(null);

            dispatch(setTempCarDeliveryConfig(null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryDestination]);

    // When a dealer or location is selected, reset the shown results and input query.
    useEffect(() => {
        if (tempDeliveryConfig?.address) {
            setDealerResults([]);
            setMapboxResults([]);
            setSearchQuery("");
            setErrorLabel(null);
        }
    }, [tempDeliveryConfig]);

    /**
     * Fetches the dealer search results from the API.
     * We use a debounced effect to prevent unnecessary requests.
     */
    useDebouncedEffect(
        () => {
            if (searchQuery.length > 2) {
                setLoading(true);
                setDealerResults([]);
                setMapboxResults([]);
                setErrorLabel(null);

                /**
                 * Fetches dealers from A2D.
                 * We first geocode the query using Mapbox, and then use the lat & lon of the first result to fetch the dealers,
                 * sorted by distance using the lat & lon.
                 */
                const searchDealers = async (): Promise<void> => {
                    // Get the lat & lon of the query
                    const mapboxGeoResults = await toyotaMap.geocode(searchQuery, mapboxToken, country, null, false);

                    // If there are no results, we don't need to continue.
                    if (mapboxGeoResults.length === 0) {
                        setLoading(false);
                        setErrorLabel(noResultsLabel);
                    }

                    // Now, we retrieve the first result. The first result is always the most relevant,
                    // as it's the result that matches the query the best. We use this result to fetch the dealers.
                    const [lon, lat] = mapboxGeoResults[0].center;

                    // Fetch the dealers from the API
                    const dealerSearchResult = await fetchDealers(apiUrl, {
                        coords: { lat, lon },
                    });

                    // Set the results
                    setDealerResults(dealerSearchResult.results);

                    setLoading(false);

                    if (dealerSearchResult.results.length === 0) setErrorLabel(noResultsLabel);
                };

                const searchAdresses = async (): Promise<void> => {
                    const mapboxGeoResults = await toyotaMap.geocode(searchQuery, mapboxToken, country, null, false);

                    const formattedGeoResults = formatMapboxResults(mapboxGeoResults);

                    if (formattedGeoResults) setMapboxResults(formattedGeoResults);

                    setLoading(false);

                    if (formattedGeoResults?.length === 0) setErrorLabel(noResultsLabel);
                };

                try {
                    if (deliveryDestination === CarDeliveryDestination.Dealer) searchDealers();
                    else if (deliveryDestination === CarDeliveryDestination.Home) searchAdresses();
                } catch (error) {
                    setErrorLabel(resultsErrorLabel);
                    Debug.error(error as string);
                    setLoading(false);
                }
            } else {
                setDealerResults([]);
                setMapboxResults([]);
            }
        },
        700,
        [searchQuery, deliveryDestination],
    );

    const onSearchInputClear = (): void => {
        setSearchQuery("");
        dispatch(setTempCarDeliveryConfig(null));
    };

    const removeDeliveryLocation = (): void => {
        // Check if the user is trying to remove the delivery location when they're editing
        // the configuration. If so, we need to reset the delivery config (not the temp one).
        if (!tempDeliveryConfig) {
            dispatch(setCarDeliveryConfig(null));
        }

        dispatch(setTempCarDeliveryConfig(null));

        // We return to the first screen if the user removes the delivery location when
        // they're on the transportation step.
        if (navStep === CarDeliveryNavStep.Transportation) {
            dispatch(setCarDeliveryModalNavStep(CarDeliveryNavStep.Destination));
        }
    };

    /**
     * Handle click on a dealer result
     */
    const dealerLocationClick = (result: DealerResultType): void => {
        // We dispatch a temporary config here since the final config is only set once
        // the user confirms the configuration on the transportation step.
        // However, since we still need the previous config to calculate the price (and allow the user to return to the previous step),
        // we need to store the temporary config in the store.
        dispatch(
            setTempCarDeliveryConfig({
                dealerName: result.name,
                dealerId: result.id,
                transportationType: deliveryTransportation,
                destination: deliveryDestination,
                address: {
                    address: result.address.line1,
                    city: result.address.city,
                    zip: result.address.zip,
                    geo: result.geoLocation,
                },
            }),
        );
        setDealerResults([]);
        setMapboxResults([]);
        setSearchQuery("");
    };

    const mapboxLocationClick = (result: FormattedMapboxResultType): void => {
        dispatch(
            setTempCarDeliveryConfig({
                address: {
                    address: result.address,
                    city: result.city,
                    zip: result.zip,
                    geo: result.geo,
                },
                destination: deliveryDestination,
            }),
        );
        setDealerResults([]);
        setMapboxResults([]);
        setSearchQuery("");
    };

    const handleBackButtonClick = (): void => {
        if (navStep === CarDeliveryNavStep.Transportation) {
            dispatch(setCarDeliveryModalNavStep(CarDeliveryNavStep.Destination));
        } else {
            dispatch(hideModal(MODAL_USC_CAR_DELIVERY));
        }
    };

    /**
     * Handle the confirmation of the delivery destination configuration.
     * This will dispatch the selected dealer to the redux store.
     */
    const confirmDestinationConfiguration = async (): Promise<void> => {
        if (!tempDeliveryConfig && !deliveryConfig) return;
        if (!currentDealer) return;

        dispatch(setCarDeliveryModalNavStep(CarDeliveryNavStep.Transportation));

        // We show the transportation modal before fetching the Driiveme prices,
        // so the rendering isn't blocked since it has to be awaited.
        // The UI handles this by showing a loading spinner.

        // Fetch delivery cost Prices
        const deliveryCostPrices = await fetchDeliveryCostPrices(
            currentDealer,
            // tempDeliveryConfig if the user is creating their configuration for the first time,
            // deliveryConfig if the user is editing their configuration.
            (tempDeliveryConfig as UscCarDeliveryConfig) || deliveryConfig,
            commonSettings,
        );

        if ("error" in deliveryCostPrices) {
            dispatch(setCarDeliveryError(true));
        } else {
            const { truck, professionalDriver } = deliveryCostPrices;
            dispatch(
                setTempCarDeliveryConfig({
                    ...((tempDeliveryConfig as UscCarDeliveryConfig) || deliveryConfig),
                    prices: {
                        truck,
                        professionalDriver,
                    },
                }),
            );
        }
    };

    /**
     * Handle the confirmation of the delivery destination configuration.
     * This is also the final step of the configurator.
     */
    const confirmTransportationConfiguration = async (): Promise<void> => {
        if (!tempDeliveryConfig) return;

        const newDeliveryConfig = {
            ...tempDeliveryConfig,
            price: tempDeliveryConfig.prices?.[deliveryTransportation] || {
                cashPriceExclVat: 0,
                cashPriceInclVat: 0,
            },
        };

        // The prices in tempDeliveryConfig are only used to store the prices we fetched from the API.
        // The final deliveryConfig should not contain the prices since it only contains the price for the selected transportation type.
        delete newDeliveryConfig.prices;

        trackCarDeliveryConfirmDelivery(uscEnv, newDeliveryConfig as UscCarDeliveryConfig, commonSettings);

        // Reset the modal state as we're done with the configurator.
        dispatch(hideModal(MODAL_USC_CAR_DELIVERY));
        dispatch(setCarDeliveryModalNavStep(CarDeliveryNavStep.Destination));
        dispatch(setCarDeliveryConfig(newDeliveryConfig as UscCarDeliveryConfig));

        dispatch(setTempCarDeliveryConfig(null));
    };

    return {
        mapboxResults,
        dealerResults,
        currentDealer,
        searchQuery,
        loading,
        errorLabel,
        onSearchInputChange,
        onSearchInputClear,
        dealerLocationClick,
        mapboxLocationClick,
        handleBackButtonClick,
        removeDeliveryLocation,
        confirmDestinationConfiguration,
        confirmTransportationConfiguration,
    };
};

export default useCarDeliveryConfigurator;
